import { Link, useLocation } from 'react-router-dom'
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

interface FooterLink {
  to: string
  label: string
}

const quickLinks: FooterLink[] = [
  { to: '/catalogo', label: 'Catálogo' },
  { to: '/sucursales', label: 'Sucursales' },
  { to: '/financiamiento', label: 'Financiamiento' },
  { to: '/nosotros', label: 'Sobre Nosotros' },
]

const legalLinks: FooterLink[] = [
  { to: '/privacidad', label: 'Política de Privacidad' },
  { to: '/terminos', label: 'Términos y Condiciones' },
  { to: '/garantia', label: 'Política de Garantía' },
  { to: '/devoluciones', label: 'Política de Devoluciones' },
]

function SocialLinks({ className }: { className?: string }) {
  return (
    <div className={className}>
      <a
        href="https://facebook.com"
        target="_blank"
        rel="noreferrer"
        className="hover:text-primary transition-colors"
        aria-label="Facebook"
      >
        <Facebook className="h-5 w-5" />
      </a>
      <a
        href="https://instagram.com"
        target="_blank"
        rel="noreferrer"
        className="hover:text-primary transition-colors"
        aria-label="Instagram"
      >
        <Instagram className="h-5 w-5" />
      </a>
      <a
        href="https://youtube.com"
        target="_blank"
        rel="noreferrer"
        className="hover:text-primary transition-colors"
        aria-label="YouTube"
      >
        <Youtube className="h-5 w-5" />
      </a>
    </div>
  )
}

function ContactList({ compact = false }: { compact?: boolean }) {
  return (
    <ul className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <li className="flex items-start gap-2">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-300" />
        <span className="text-xs text-gray-300 sm:text-sm">
          Av. Alberto Caamaño Deño #144, San Pedro de Macorís, República Dominicana
        </span>
      </li>
      <li className="flex items-center gap-2">
        <Phone className="h-4 w-4 shrink-0 text-gray-300" />
        <a
          href="tel:+18092468383"
          className="text-xs text-gray-300 transition-colors hover:text-white sm:text-sm"
        >
          (809) 246-8383
        </a>
      </li>
      <li className="flex items-center gap-2">
        <Mail className="h-4 w-4 shrink-0 text-gray-300" />
        <a
          href="mailto:info@orientalramirez.com"
          className="break-all text-xs text-gray-300 transition-colors hover:text-white sm:text-sm"
        >
          info@orientalramirez.com
        </a>
      </li>
    </ul>
  )
}

function LinkList({ links }: { links: FooterLink[] }) {
  return (
    <ul className="space-y-2">
      {links.map((link) => (
        <li key={link.to}>
          <Link
            to={link.to}
            className="text-sm text-gray-300 transition-colors hover:text-white"
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  )
}

export function Footer() {
  const location = useLocation()

  if (
    location.pathname === '/catalogo' ||
    location.pathname.startsWith('/repuestos')
  ) {
    return null
  }

  return (
    <footer className="bg-[#38367D] text-white">
      {/* ===== Mobile (compact) ===== */}
      <div className="lg:hidden">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 sm:px-6">
          {/* Brand */}
          <div className="flex items-center justify-between gap-4">
            <img
              src="/logo.png"
              alt="Oriental Ramirez"
              className="h-auto w-24 max-w-[120px]"
            />
            <SocialLinks className="flex gap-4" />
          </div>
          <p className="mt-3 text-xs text-gray-300">
            Más de 30 años siendo líderes en la venta de motocicletas y
            vehículos en República Dominicana.
          </p>
        </div>

        {/* Collapsible link sections */}
        <Accordion type="single" collapsible className="mt-2 px-4 sm:px-6">
          <AccordionItem value="quick-links" className="border-white/10">
            <AccordionTrigger className="py-3 text-sm font-semibold hover:no-underline">
              Enlaces Rápidos
            </AccordionTrigger>
            <AccordionContent>
              <LinkList links={quickLinks} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="legal" className="border-white/10">
            <AccordionTrigger className="py-3 text-sm font-semibold hover:no-underline">
              Legal
            </AccordionTrigger>
            <AccordionContent>
              <LinkList links={legalLinks} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="contact" className="border-b-0 border-white/10">
            <AccordionTrigger className="py-3 text-sm font-semibold hover:no-underline">
              Contacto
            </AccordionTrigger>
            <AccordionContent>
              <ContactList compact />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* ===== Desktop (full 4-column) ===== */}
      <div className="hidden lg:block">
        <div className="mx-auto w-full max-w-7xl px-8 py-14">
          <div className="grid grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <img
                src="/logo.png"
                alt="Oriental Ramirez"
                className="h-auto w-32 max-w-[150px]"
              />
              <p className="text-sm text-gray-300">
                Más de 30 años siendo líderes en la venta de motocicletas y
                vehículos en República Dominicana.
              </p>
              <SocialLinks className="flex gap-4" />
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="mb-3 text-lg font-semibold">Enlaces Rápidos</h3>
              <LinkList links={quickLinks} />
            </div>

            {/* Legal */}
            <div>
              <h3 className="mb-3 text-lg font-semibold">Legal</h3>
              <LinkList links={legalLinks} />
            </div>

            {/* Contact */}
            <div>
              <h3 className="mb-3 text-lg font-semibold">Contacto</h3>
              <ContactList />
            </div>
          </div>
        </div>
      </div>

      {/* ===== Bottom Footer (compact) ===== */}
      <div className="border-t border-white/10">
        <div className="mx-auto w-full max-w-7xl px-4 py-3 sm:px-6 lg:px-8 lg:py-5">
          <div className="flex flex-col items-center justify-between gap-1.5 text-center md:flex-row md:gap-3 md:text-left">
            <div className="text-[11px] text-gray-300 sm:text-sm">
              © {new Date().getFullYear()} Oriental Ramirez. Todos los derechos
              reservados.
            </div>
            <div className="text-[11px] text-gray-300 sm:text-sm">
              <span>Desarrollado por </span>
              <a
                href="https://ixi.do"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4 hover:text-white"
              >
                ixi Dominicana
              </a>
            </div>
            <div className="text-[10px] text-gray-400 sm:text-xs">
              Algunas imágenes pueden ser ejemplos. Consulte con su vendedor
              asignado.
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
